import type { FulfillmentOrderRow } from '@/infrastructure/FulfillmentAdminClient';
import { shippingAddressOf } from '@/utils/fulfillmentShippingAddress';

/**
 * The list's customer/shipping/amounts cells read decoded JSON columns whose shape differs by ingestion
 * source (WooCommerce mirror, CSV import, CreatorHub). These readers pick the first key that is present
 * rather than assuming one writer, so a missing field renders as an em dash instead of breaking the row.
 */
export const EMPTY_CELL = '—';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const firstText = (source: Record<string, unknown> | null, keys: string[]): string | null => {
  for (const key of keys ?? []) {
    const value = source?.[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
};

/**
 * A dense cell reads as two lines — what the operator scans for on top, the qualifier underneath in
 * muted type. One flat line of identical text gives the eye nothing to catch, which is what made the
 * first version of this list unreadable.
 */
export interface StackedCell {
  primary: string;
  secondary: string | null;
}

/** Customer name over its email, falling back to the email alone when the order carries no name. */
export const describeCustomer = (order: FulfillmentOrderRow): StackedCell => {
  const customer = asRecord(asRecord(order.customer)?.['customer']) ?? asRecord(order.customer);
  const email = firstText(customer, ['email']);
  const name =
    firstText(customer, ['name', 'fullName']) ||
    [firstText(customer, ['firstName']), firstText(customer, ['lastName'])].filter(Boolean).join(' ').trim();
  if (name) {
    return { primary: name, secondary: email };
  }
  return { primary: email ?? EMPTY_CELL, secondary: null };
};

/**
 * City over country; the full street address belongs on the detail screen, not in a list cell. The carrier's consignee
 * first, else the address the carrier receives, which for a marketing order is its flat recipient (SSD-7512).
 */
export const describeShippingAddress = (order: FulfillmentOrderRow): StackedCell => {
  const shipping = asRecord(order.shipping);
  const target = asRecord(shipping?.['consignee']) ?? shippingAddressOf(order.shipping, order.customer);
  const city = firstText(target, ['city']);
  const country = firstText(target, ['countryCode', 'country']);
  const state = firstText(target, ['state']);
  // A city-state repeats itself across state and country; showing `SG, SG` reads as a data bug.
  const region = [...new Set([state, country].filter((part): part is string => Boolean(part)))].join(', ');
  if (city) {
    return { primary: city, secondary: region || null };
  }
  return { primary: region || EMPTY_CELL, secondary: null };
};

/** Mirrors the OMS `Total Value (Discount)` cell: the charged total, with the discount in parentheses. */
export const describeTotalValue = (order: FulfillmentOrderRow): string => {
  const amounts = asRecord(order.amounts);
  const total = Number(amounts?.['totalAmount']);
  if (!Number.isFinite(total)) {
    return EMPTY_CELL;
  }
  const currency = firstText(amounts, ['currency']) ?? '';
  const discount = Number(amounts?.['discount']);
  const discountSuffix = Number.isFinite(discount) && discount > 0 ? ` (${discount})` : '';
  return `${currency ? `${currency} ` : ''}${total}${discountSuffix}`.trim();
};
